// src/config/standards/digitalCitizenship.js

// Digital Citizenship Standards (aligned with widely adopted K–12 frameworks;
// descriptions are concise/original, not verbatim from any proprietary source).
// Grade bands:
//   K–2 share one band
//   3–5 share one band
//   6–8 share one band
//   High School has clusters: "HS—9–10" and "HS—11–12" within each domain

const dcItem = (grade, code, description) => ({
  id: `Digital Citizenship-${grade}-${code}`,
  code,
  description,
});

// -------------------------------
// K–2
// -------------------------------
const K2 = {
  "Media Balance & Well-Being": [
    dcItem("K–2", "DC.K-2.MB.1", "Recognize that devices are tools and identify times to use them and times to take a break."),
    dcItem("K–2", "DC.K-2.MB.2", "Name simple signs that screen time may be too much (e.g., tired eyes, missing play time)."),
    dcItem("K–2", "DC.K-2.MB.3", "Choose healthy, non-screen activities to balance technology use."),
  ],
  "Privacy & Security": [
    dcItem("K–2", "DC.K-2.PS.1", "Keep passwords and personal information private; ask an adult before sharing."),
    dcItem("K–2", "DC.K-2.PS.2", "Recognize pop-ups/links and know to ask for help before clicking."),
    dcItem("K–2", "DC.K-2.PS.3", "Explain why it’s important to log off or close apps when finished."),
  ],
  "Digital Footprint & Identity": [
    dcItem("K–2", "DC.K-2.DF.1", "Explain the difference between personal information and private information."),
    dcItem("K–2", "DC.K-2.DF.2", "Describe how posts or pictures can stay online and be seen by others."),
    dcItem("K–2", "DC.K-2.DF.3", "Use kind words and images to help create a positive online identity."),
  ],
  "Relationships & Communication": [
    dcItem("K–2", "DC.K-2.RC.1", "Practice polite language and tone when messaging or commenting."),
    dcItem("K–2", "DC.K-2.RC.2", "Ask permission before taking or sharing someone’s photo or work."),
    dcItem("K–2", "DC.K-2.RC.3", "Tell a trusted adult if an online message or image feels confusing or unsafe."),
  ],
  "Cyberbullying, Digital Drama & Hate Speech": [
    dcItem("K–2", "DC.K-2.CB.1", "Identify unkind behavior online and explain how it can hurt feelings."),
    dcItem("K–2", "DC.K-2.CB.2", "Practice being an upstander by getting help from an adult."),
    dcItem("K–2", "DC.K-2.CB.3", "Describe simple steps to stay safe (ignore, save, tell a trusted adult)."),
  ],
  "News & Media Literacy": [
    dcItem("K–2", "DC.K-2.NM.1", "Tell the difference between a story and an advertisement at a basic level."),
    dcItem("K–2", "DC.K-2.NM.2", "Ask, “Who made this and why?” when looking at media."),
    dcItem("K–2", "DC.K-2.NM.3", "Sort information by topic with help from an adult or teacher."),
  ],
  "Creative Credit & Copyright": [
    dcItem("K–2", "DC.K-2.CC.1", "Give credit when using the work of others (e.g., saying the author’s name)."),
    dcItem("K–2", "DC.K-2.CC.2", "Use teacher-provided safe images, music, or clips in projects."),
    dcItem("K–2", "DC.K-2.CC.3", "Explain why copying someone’s work without permission isn’t fair."),
  ],
  "Digital Rights & Responsibilities": [
    dcItem("K–2", "DC.K-2.RR.1", "Follow classroom rules for using devices and handling them with care."),
    dcItem("K–2", "DC.K-2.RR.2", "Use respectful behavior online just as in person."),
    dcItem("K–2", "DC.K-2.RR.3", "Describe what to do if a rule is broken or a device is damaged."),
  ],
};

// -------------------------------
// 3–5
// -------------------------------
const G3_5 = {
  "Media Balance & Well-Being": [
    dcItem("3–5", "DC.3-5.MB.1", "Set personal goals for balanced tech use and reflect on progress."),
    dcItem("3–5", "DC.3-5.MB.2", "Explain how screen time can affect sleep, mood, and relationships."),
    dcItem("3–5", "DC.3-5.MB.3", "Apply strategies (breaks, timers, no-device zones) to maintain balance."),
  ],
  "Privacy & Security": [
    dcItem("3–5", "DC.3-5.PS.1", "Create strong passwords and explain why they protect accounts."),
    dcItem("3–5", "DC.3-5.PS.2", "Recognize phishing attempts and suspicious links; know how to respond."),
    dcItem("3–5", "DC.3-5.PS.3", "Adjust basic privacy settings with adult guidance (profiles, shared docs)."),
  ],
  "Digital Footprint & Identity": [
    dcItem("3–5", "DC.3-5.DF.1", "Describe a digital footprint and how actions build an online reputation."),
    dcItem("3–5", "DC.3-5.DF.2", "Make choices that reflect personal values when posting or commenting."),
    dcItem("3–5", "DC.3-5.DF.3", "Explain how sharing others’ content affects both your and their footprint."),
  ],
  "Relationships & Communication": [
    dcItem("3–5", "DC.3-5.RC.1", "Use appropriate tools/platforms for different kinds of communication."),
    dcItem("3–5", "DC.3-5.RC.2", "Demonstrate empathy, tone awareness, and active listening online."),
    dcItem("3–5", "DC.3-5.RC.3", "Compare public vs. private spaces online and choose the right setting."),
  ],
  "Cyberbullying, Digital Drama & Hate Speech": [
    dcItem("3–5", "DC.3-5.CB.1", "Identify cyberbullying and digital drama; distinguish conflict from bullying."),
    dcItem("3–5", "DC.3-5.CB.2", "Use upstander strategies: support target, save evidence, seek help."),
    dcItem("3–5", "DC.3-5.CB.3", "Explain school procedures for reporting harmful online behavior."),
  ],
  "News & Media Literacy": [
    dcItem("3–5", "DC.3-5.NM.1", "Evaluate sources for purpose and audience; identify advertisements/sponsored content."),
    dcItem("3–5", "DC.3-5.NM.2", "Compare two sources on the same topic and note agreements/disagreements."),
    dcItem("3–5", "DC.3-5.NM.3", "Use multiple sources to check facts before sharing."),
  ],
  "Creative Credit & Copyright": [
    dcItem("3–5", "DC.3-5.CC.1", "Explain the basics of copyright and Creative Commons in student-friendly terms."),
    dcItem("3–5", "DC.3-5.CC.2", "Attribute images, music, and text used in projects."),
    dcItem("3–5", "DC.3-5.CC.3", "Remix media legally by following license terms and giving credit."),
  ],
  "Digital Rights & Responsibilities": [
    dcItem("3–5", "DC.3-5.RR.1", "Summarize school technology policies and acceptable use expectations."),
    dcItem("3–5", "DC.3-5.RR.2", "Describe student rights and responsibilities when using shared devices."),
    dcItem("3–5", "DC.3-5.RR.3", "Explain why accessibility and inclusivity matter in digital spaces."),
  ],
};

// -------------------------------
// 6–8
// -------------------------------
const G6_8 = {
  "Media Balance & Well-Being": [
    dcItem("6–8", "DC.6-8.MB.1", "Analyze personal media habits and create a balanced use plan."),
    dcItem("6–8", "DC.6-8.MB.2", "Describe how notifications, autoplay, and streaks influence behavior."),
    dcItem("6–8", "DC.6-8.MB.3", "Apply strategies (focus modes, limits) to support well-being and productivity."),
  ],
  "Privacy & Security": [
    dcItem("6–8", "DC.6-8.PS.1", "Use multi-factor authentication and strong passphrases; manage a password manager."),
    dcItem("6–8", "DC.6-8.PS.2", "Configure privacy settings; explain risks of public Wi-Fi and data tracking."),
    dcItem("6–8", "DC.6-8.PS.3", "Identify phishing, scams, and social engineering; outline response steps."),
  ],
  "Digital Footprint & Identity": [
    dcItem("6–8", "DC.6-8.DF.1", "Audit your online presence and assess potential long-term effects."),
    dcItem("6–8", "DC.6-8.DF.2", "Set boundaries for what, when, and with whom you share online."),
    dcItem("6–8", "DC.6-8.DF.3", "Build a positive digital identity aligned with personal values and goals."),
  ],
  "Relationships & Communication": [
    dcItem("6–8", "DC.6-8.RC.1", "Analyze how tone and context can be misread in digital communication."),
    dcItem("6–8", "DC.6-8.RC.2", "Resolve conflicts online using respectful language and agreed-upon norms."),
    dcItem("6–8", "DC.6-8.RC.3", "Follow community guidelines; participate constructively in group spaces."),
  ],
  "Cyberbullying, Digital Drama & Hate Speech": [
    dcItem("6–8", "DC.6-8.CB.1", "Recognize harassment, hate speech, and targeted attacks; differentiate severity."),
    dcItem("6–8", "DC.6-8.CB.2", "Document incidents, preserve evidence, report appropriately, and support targets."),
    dcItem("6–8", "DC.6-8.CB.3", "Discuss legal/ethical implications and the role of bystanders/upstanders."),
  ],
  "News & Media Literacy": [
    dcItem("6–8", "DC.6-8.NM.1", "Differentiate misinformation, disinformation, and opinion; identify bias."),
    dcItem("6–8", "DC.6-8.NM.2", "Explain how algorithms and recommendations shape what you see."),
    dcItem("6–8", "DC.6-8.NM.3", "Use fact-checking strategies (lateral reading, reverse image search concepts)."),
  ],
  "Creative Credit & Copyright": [
    dcItem("6–8", "DC.6-8.CC.1", "Explain copyright/fair use and Creative Commons license types and conditions."),
    dcItem("6–8", "DC.6-8.CC.2", "Choose properly licensed media; provide clear attributions and citations."),
    dcItem("6–8", "DC.6-8.CC.3", "Describe ethical remixing and derivative works; respect authors’ terms."),
  ],
  "Digital Rights & Responsibilities": [
    dcItem("6–8", "DC.6-8.RR.1", "Summarize student-relevant laws/policies (e.g., AUPs; basics of COPPA/FERPA/CIPA)."),
    dcItem("6–8", "DC.6-8.RR.2", "Discuss equity, access, and inclusion issues in digital participation."),
    dcItem("6–8", "DC.6-8.RR.3", "Engage in civic participation online responsibly and respectfully."),
  ],
};

// -------------------------------
// HIGH SCHOOL: clusters per domain
// -------------------------------
const HS_9_10 = {
  "Media Balance & Well-Being": [
    dcItem("HS—9–10", "DC.HS-9-10.MB.1", "Evaluate trade-offs between productivity and persuasive platform design."),
    dcItem("HS—9–10", "DC.HS-9-10.MB.2", "Design a personal media plan that supports long-term health and goals."),
    dcItem("HS—9–10", "DC.HS-9-10.MB.3", "Apply evidence-based strategies to reduce distraction and optimize focus."),
  ],
  "Privacy & Security": [
    dcItem("HS—9–10", "DC.HS-9-10.PS.1", "Assess privacy policies and permissions; configure robust privacy/security settings."),
    dcItem("HS—9–10", "DC.HS-9-10.PS.2", "Explain common tracking technologies (cookies, device IDs) and limit exposure."),
    dcItem("HS—9–10", "DC.HS-9-10.PS.3", "Perform basic risk assessment (threats, attack surfaces) and mitigation steps."),
  ],
  "Digital Footprint & Identity": [
    dcItem("HS—9–10", "DC.HS-9-10.DF.1", "Curate a positive, professional online presence (profiles, portfolios)."),
    dcItem("HS—9–10", "DC.HS-9-10.DF.2", "Explain digital permanence and potential impacts on college and employment."),
    dcItem("HS—9–10", "DC.HS-9-10.DF.3", "Manage audience and context; separate personal and public identities when needed."),
  ],
  "Relationships & Communication": [
    dcItem("HS—9–10", "DC.HS-9-10.RC.1", "Adapt communication for diverse audiences and platforms; maintain professionalism."),
    dcItem("HS—9–10", "DC.HS-9-10.RC.2", "Lead or moderate online communities using clear norms and inclusive practices."),
    dcItem("HS—9–10", "DC.HS-9-10.RC.3", "Resolve misunderstandings across cultures and contexts with empathy."),
  ],
  "Cyberbullying, Digital Drama & Hate Speech": [
    dcItem("HS—9–10", "DC.HS-9-10.CB.1", "Identify coordinated harassment patterns; distinguish policy violations from conflict."),
    dcItem("HS—9–10", "DC.HS-9-10.CB.2", "Apply reporting/escalation pathways; preserve evidence appropriately."),
    dcItem("HS—9–10", "DC.HS-9-10.CB.3", "Propose prevention/education strategies for the school community."),
  ],
  "News & Media Literacy": [
    dcItem("HS—9–10", "DC.HS-9-10.NM.1", "Evaluate credibility using sourcing, evidence, and corroboration across outlets."),
    dcItem("HS—9–10", "DC.HS-9-10.NM.2", "Explain filter bubbles and assess how personalization affects viewpoint diversity."),
    dcItem("HS—9–10", "DC.HS-9-10.NM.3", "Identify manipulated media (e.g., deepfakes) and cite verification strategies."),
  ],
  "Creative Credit & Copyright": [
    dcItem("HS—9–10", "DC.HS-9-10.CC.1", "Apply fair-use factors in common school scenarios and justify decisions."),
    dcItem("HS—9–10", "DC.HS-9-10.CC.2", "Select appropriate licenses for original work and attribute others’ work correctly."),
    dcItem("HS—9–10", "DC.HS-9-10.CC.3", "Compare open licenses (e.g., CC variants) and implications for sharing/remixing."),
  ],
  "Digital Rights & Responsibilities": [
    dcItem("HS—9–10", "DC.HS-9-10.RR.1", "Explain key student-relevant laws/policies and their practical implications."),
    dcItem("HS—9–10", "DC.HS-9-10.RR.2", "Evaluate accessibility and inclusion in digital products; propose improvements."),
    dcItem("HS—9–10", "DC.HS-9-10.RR.3", "Participate in digital advocacy while respecting rights, safety, and school policy."),
  ],
};

const HS_11_12 = {
  "Media Balance & Well-Being": [
    dcItem("HS—11–12", "DC.HS-11-12.MB.1", "Critique persuasive design and quantify its effects on attention and behavior."),
    dcItem("HS—11–12", "DC.HS-11-12.MB.2", "Engineer and evaluate a personal attention/energy management system."),
    dcItem("HS—11–12", "DC.HS-11-12.MB.3", "Mentor peers on sustainable tech habits and digital well-being."),
  ],
  "Privacy & Security": [
    dcItem("HS—11–12", "DC.HS-11-12.PS.1", "Compare privacy trade-offs across platforms/services; justify configuration choices."),
    dcItem("HS—11–12", "DC.HS-11-12.PS.2", "Explain data brokers and digital dossiers; practice data-minimization strategies."),
    dcItem("HS—11–12", "DC.HS-11-12.PS.3", "Develop an incident response plan for common personal security threats."),
  ],
  "Digital Footprint & Identity": [
    dcItem("HS—11–12", "DC.HS-11-12.DF.1", "Develop a coherent personal brand narrative across platforms."),
    dcItem("HS—11–12", "DC.HS-11-12.DF.2", "Assess options for removing or counter-messaging harmful content."),
    dcItem("HS—11–12", "DC.HS-11-12.DF.3", "Evaluate long-term implications of data permanence for career and civic life."),
  ],
  "Relationships & Communication": [
    dcItem("HS—11–12", "DC.HS-11-12.RC.1", "Lead inclusive, cross-cultural collaboration in digital communities."),
    dcItem("HS—11–12", "DC.HS-11-12.RC.2", "Apply advanced moderation/mediation strategies to sustain healthy discourse."),
    dcItem("HS—11–12", "DC.HS-11-12.RC.3", "Produce communication artifacts tailored to diverse stakeholders and goals."),
  ],
  "Cyberbullying, Digital Drama & Hate Speech": [
    dcItem("HS—11–12", "DC.HS-11-12.CB.1", "Design prevention campaigns that address harassment, hate speech, and harm."),
    dcItem("HS—11–12", "DC.HS-11-12.CB.2", "Outline evidence-preservation and reporting steps with attention to legal/ethical factors."),
    dcItem("HS—11–12", "DC.HS-11-12.CB.3", "Evaluate restorative vs. punitive responses and their community impacts."),
  ],
  "News & Media Literacy": [
    dcItem("HS—11–12", "DC.HS-11-12.NM.1", "Analyze manipulative techniques and rhetorical strategies in media."),
    dcItem("HS—11–12", "DC.HS-11-12.NM.2", "Use advanced verification (lateral reading, source metadata) to vet claims."),
    dcItem("HS—11–12", "DC.HS-11-12.NM.3", "Create media that is transparent about sources, evidence, and limitations."),
  ],
  "Creative Credit & Copyright": [
    dcItem("HS—11–12", "DC.HS-11-12.CC.1", "Choose licenses for your work; compare implications for distribution and remix."),
    dcItem("HS—11–12", "DC.HS-11-12.CC.2", "Explain IP considerations for collaborations, internships, or entrepreneurship."),
    dcItem("HS—11–12", "DC.HS-11-12.CC.3", "Produce professional-quality attributions/citations across media types."),
  ],
  "Digital Rights & Responsibilities": [
    dcItem("HS—11–12", "DC.HS-11-12.RR.1", "Critically evaluate technology policies and propose concrete revisions."),
    dcItem("HS—11–12", "DC.HS-11-12.RR.2", "Plan ethical digital advocacy/activism balancing impact, safety, and compliance."),
    dcItem("HS—11–12", "DC.HS-11-12.RR.3", "Assess equity, accessibility (e.g., WCAG concepts), and bias in digital systems."),
  ],
};

// -------------------------------
// Export full subject map
// -------------------------------
export const DIGITAL_CITIZENSHIP_STANDARDS = {
  "Digital Citizenship": {
    "Kindergarten": K2,
    "1st Grade": K2,
    "2nd Grade": K2,

    "3rd Grade": G3_5,
    "4th Grade": G3_5,
    "5th Grade": G3_5,

    "6th Grade": G6_8,
    "7th Grade": G6_8,
    "8th Grade": G6_8,

    "High School": {
      "Media Balance & Well-Being": {
        "HS—9–10": HS_9_10["Media Balance & Well-Being"],
        "HS—11–12": HS_11_12["Media Balance & Well-Being"],
      },
      "Privacy & Security": {
        "HS—9–10": HS_9_10["Privacy & Security"],
        "HS—11–12": HS_11_12["Privacy & Security"],
      },
      "Digital Footprint & Identity": {
        "HS—9–10": HS_9_10["Digital Footprint & Identity"],
        "HS—11–12": HS_11_12["Digital Footprint & Identity"],
      },
      "Relationships & Communication": {
        "HS—9–10": HS_9_10["Relationships & Communication"],
        "HS—11–12": HS_11_12["Relationships & Communication"],
      },
      "Cyberbullying, Digital Drama & Hate Speech": {
        "HS—9–10": HS_9_10["Cyberbullying, Digital Drama & Hate Speech"],
        "HS—11–12": HS_11_12["Cyberbullying, Digital Drama & Hate Speech"],
      },
      "News & Media Literacy": {
        "HS—9–10": HS_9_10["News & Media Literacy"],
        "HS—11–12": HS_11_12["News & Media Literacy"],
      },
      "Creative Credit & Copyright": {
        "HS—9–10": HS_9_10["Creative Credit & Copyright"],
        "HS—11–12": HS_11_12["Creative Credit & Copyright"],
      },
      "Digital Rights & Responsibilities": {
        "HS—9–10": HS_9_10["Digital Rights & Responsibilities"],
        "HS—11–12": HS_11_12["Digital Rights & Responsibilities"],
      },
    },
  },
};

export default DIGITAL_CITIZENSHIP_STANDARDS;
